/**
 * The arm-space declaration on the SCENE (ADR-030 §13, ruled 2026-08-28):
 * read and write what the file declares on each upper-arm node. Arm-space is
 * a torso-axis abduction AT PLAY — it never touches the bind — so the only
 * way it reaches World, or survives a reopen here, is a declaration in glTF
 * `extras` that a reader applies every frame. three carries userData ↔
 * extras both ways, so nothing here touches bytes; the export door writes the
 * scene and the markers ride with the nodes. The record's shape and parse are
 * the kernel's (arm-space.ts); this module resolves WHICH nodes — inside the
 * selected skeleton, the one rule every door applies.
 *
 * Reading: every declared arm must agree on the CHARACTER value (a file whose
 * arms disagree is inconsistent, refused); an arm with no marker while the
 * other has one is refused the same way. Absent on every arm → null.
 *
 * Fail-loud: a bone the selected skeleton does not bind throws; a malformed
 * marker refuses through the kernel parse (ArmSpaceRefusal, naming the bone).
 *
 * Depends on: three, convert-rig (the selected-skeleton seam), kernel.
 * Depended on by: the app session driver (run-document) and the app's export door.
 */
import type { Object3D } from 'three';
import {
  ArmSpaceRefusal,
  parseArmSpaceMarker,
  withArmSpaceMarker,
  type ArmSpaceMarker,
} from '@harryhausen/kernel';
import { boneOfSelectedSkeleton } from './convert-rig.js';
import { AdapterError } from './errors.js';

export class SceneArmSpaceMarkerError extends AdapterError {}

interface Selection {
  readonly label: string;
  readonly selectSkeleton?: string;
}

function armNode(root: Object3D, boneName: string, options: Selection): Object3D {
  const node = boneOfSelectedSkeleton(root, boneName, options);
  if (node === undefined) {
    throw new SceneArmSpaceMarkerError(
      `no arm-space marker door for "${boneName}" — not a bone of the selected skeleton ("${options.label}").`,
    );
  }
  return node;
}

/** What each bone SHOULD declare for a character value — the caller's derivation (armSpacePlan on the rig). */
export type ArmSpacePlanFor = (character: number) => readonly { readonly bone: string; readonly degrees: number; readonly axis: { readonly x: number; readonly y: number; readonly z: number } }[];

/** Per-bone tolerance: a declaration is the plan's to a thousandth of a degree and an axis to 1e-6 rad. */
const DEGREES_TOLERANCE = 1e-3;
const AXIS_DOT_TOLERANCE = 1e-6;

/**
 * The character's arm-space the file declares across `bones` (both upper
 * arms), or null when none of them declares one. Refuses an inconsistent
 * file: an arm declared while the other is not; arms disagreeing on the
 * character value; or — Codex P2 on #131 — a bone whose own `degrees`/`axis`
 * is not what `planFor(character)` derives for it. Two readers act on this
 * file: this app previews the plan for `character`, World applies the
 * per-bone declarations; they must be the same fact, or the next export
 * would silently replace what the file said.
 */
export function readSceneArmSpace(
  root: Object3D,
  bones: readonly string[],
  options: Selection,
  planFor: ArmSpacePlanFor,
): number | null {
  const read = bones.map((bone) => ({
    bone,
    marker: parseArmSpaceMarker(armNode(root, bone, options).userData, `"${bone}" of "${options.label}"`),
  }));
  const declared = read.filter((r): r is { bone: string; marker: ArmSpaceMarker } => r.marker !== null);
  if (declared.length === 0) return null;
  if (declared.length !== read.length) {
    throw new ArmSpaceRefusal(
      `"${options.label}" declares arm-space on ${declared.map((d) => d.bone).join(', ')} but not on ` +
        `${read.filter((r) => r.marker === null).map((r) => r.bone).join(', ')} — the file is inconsistent`,
    );
  }
  const values = new Set(declared.map((d) => d.marker.character));
  if (values.size > 1) {
    throw new ArmSpaceRefusal(
      `"${options.label}" declares different arm-space values per arm (${declared.map((d) => `${d.bone}=${d.marker.character}`).join(', ')}) — the file is inconsistent`,
    );
  }
  const character = declared[0]!.marker.character;
  const expected = planFor(character);
  for (const { bone, marker } of declared) {
    const plan = expected.find((e) => e.bone === bone);
    if (plan === undefined) {
      throw new ArmSpaceRefusal(`"${options.label}": the plan for ${character}° names no entry for "${bone}" — cannot validate its declaration`);
    }
    const dot = marker.axis.x * plan.axis.x + marker.axis.y * plan.axis.y + marker.axis.z * plan.axis.z;
    if (Math.abs(marker.degrees - plan.degrees) > DEGREES_TOLERANCE || dot < 1 - AXIS_DOT_TOLERANCE) {
      throw new ArmSpaceRefusal(
        `"${bone}" of "${options.label}" declares ${marker.degrees.toFixed(4)}° about (${marker.axis.x}, ${marker.axis.y}, ${marker.axis.z}) ` +
          `but a character value of ${character}° puts ${plan.degrees.toFixed(4)}° about (${plan.axis.x}, ${plan.axis.y}, ${plan.axis.z}) on it — ` +
          `the file is inconsistent; a reader applying the per-bone declaration and this app would disagree`,
      );
    }
  }
  return character;
}

/** Declare arm-space on each entry's bone so the next export carries it. */
export function writeSceneArmSpace(
  root: Object3D,
  entries: readonly { readonly bone: string; readonly marker: ArmSpaceMarker }[],
  options: Selection,
): void {
  for (const { bone, marker } of entries) {
    const node = armNode(root, bone, options);
    node.userData = withArmSpaceMarker(node.userData, marker);
  }
}

/** Withdraw the declaration from `bones` (a character set back to neutral with no prior declaration to keep). */
export function clearSceneArmSpace(root: Object3D, bones: readonly string[], options: Selection): void {
  for (const bone of bones) {
    const node = armNode(root, bone, options);
    node.userData = withArmSpaceMarker(node.userData, null);
  }
}
